import React from "react";
import {
  ActionsContainer,
  Title,
  Columns,
  Content,
  Container,
  LeftContent,
  RightContent,
  TimerContainer,
  ListItem,
  ErrorWrapper,
  EmptyWrapper,
} from "./Actions.styled";
import { Typography, Card, Chip, Icon, Tag, WidgetEmptyErrorPlaceholder } from "@ann/web-components";
import ExpandableChilds from "../ExpandableChilds/ExpandableChilds";

/**
 * Get confidence tag color based on percentage
 * @param {number} confidence - Confidence percentage (0-100)
 * @returns {string} Color name for Tag component
 */
const getConfidenceColor = (confidence) => {
  if (confidence >= 80) return "success";
  if (confidence >= 50) return "warning";
  return "error";
};

/**
 * Extract displayable text from an item that could be a string or an object
 * @param {string|object} item - The item to extract text from
 * @returns {string} The displayable text
 */
const getItemText = (item) => {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    return item.title || item.content || item.description || JSON.stringify(item);
  }
  return String(item);
};

/**
 * Renders a single behavioral trait card
 */
const TraitCard = ({ trait }) => (
  <Card size="large">
    <Typography category="subtitle" size="medium" weight="bold">
      {trait.name}
    </Typography>

    <Container>
      <div>
        <LeftContent>
          <Typography category="body" size="medium" weight="regular">
            Current Value
          </Typography>
        </LeftContent>
        <RightContent>
          <TimerContainer>
            <Icon name="timer" color="#2A2A2A" size="small" />
            <span>{trait.ttlDays} days TTL</span>
          </TimerContainer>
        </RightContent>
      </div>

      <div>
        <LeftContent>
          <Chip
            size="medium"
            text={trait.currentValue || "—"}
            variant="secondary"
          />
        </LeftContent>
        <RightContent>
          <Tag
            color={getConfidenceColor(trait.confidencePct)}
            text={`${trait.confidencePct}% confidence`}
          />
        </RightContent>
      </div>

      {trait.guidance && trait.guidance.length > 0 && (
        <div>
          <ExpandableChilds renderChilds={3}>
            {trait.guidance.map((item, index) => (
              <ListItem key={index} role="listitem">
                {getItemText(item)}
              </ListItem>
            ))}
          </ExpandableChilds>
        </div>
      )}
    </Container>
  </Card>
);

/**
 * Renders a Next Best Action card
 */
const NbaCard = ({ nbaItem }) => (
  <Card size="large">
    <Typography category="subtitle" size="medium" weight="bold">
      Next Best Action
    </Typography>
    <Typography category="body" size="medium" weight="regular">
      {nbaItem.content}
    </Typography>
  </Card>
);

const Actions = ({
  hasData = false,
  loading = false,
  error = null,
  onRefresh,
  traits = [],
  nba = [],
}) => {
  if (error) {
    return (
      <ActionsContainer>
        <ErrorWrapper>
          <WidgetEmptyErrorPlaceholder
            icon="error"
            title="Something went wrong"
            message="We couldn't load snapshot actions. Please try again."
            buttonText="Retry"
            showButton={true}
            onClick={onRefresh}
          />
        </ErrorWrapper>
      </ActionsContainer>
    );
  }

  if (!hasData && !loading) {
    return (
      <ActionsContainer>
        <EmptyWrapper>
          <WidgetEmptyErrorPlaceholder
            icon="searchOff"
            title="No data to display here yet"
            message="We'll display behavioural and preference data once there's enough interaction across calls, messages, or notes."
            showButton={false}
          />
        </EmptyWrapper>
      </ActionsContainer>
    );
  }

  // Split traits into rows of 2 for layout
  const traitRows = [];
  for (let i = 0; i < traits.length; i += 2) {
    traitRows.push(traits.slice(i, i + 2));
  }

  return (
    <ActionsContainer>
      {traits.length > 0 && (
        <>
          <Title>
            <Typography category="subtitle" size="medium" weight="bold">
              Behavioral Traits
            </Typography>
          </Title>

          <Typography category="body" size="medium" weight="regular">
            Behavioral patterns and preferences identified for this customer.
          </Typography>

          <Content>
            {traitRows.map((row, rowIndex) => (
              <Columns key={rowIndex}>
                {row.map((trait, traitIndex) => (
                  <TraitCard key={`${trait.name}-${traitIndex}`} trait={trait} />
                ))}
              </Columns>
            ))}
          </Content>
        </>
      )}

      {nba.length > 0 && (
        <>
          <Title>
            <Typography category="subtitle" size="medium" weight="bold">
              Next Best Actions
            </Typography>
          </Title>

          <Typography category="body" size="medium" weight="regular">
            Recommended actions to take with this customer.
          </Typography>

          <Content>
            <Columns>
              {nba.map((nbaItem) => (
                <NbaCard key={nbaItem.id} nbaItem={nbaItem} />
              ))}
            </Columns>
          </Content>
        </>
      )}
    </ActionsContainer>
  );
};

export default Actions;
